import {FinancialCommandDialog} from "../../../shared/components/FinancialCommandDialog";
import {addMoney, formatExactMoney, normalizeMoney} from "../../../shared/finance/decimal";
import i18n, {useTranslation} from "../../../shared/i18n";
import {
    getSettlementActionPresentation,
    getSettlementStatusPresentation,
} from "../statusMachine";

const PAYMENT_ACTIONS = new Set(["partial_paid", "mark_paid"]);

const commandFields = (action, t) => {
    const reasonField = {name: "reason", label: t("money.settlements.dialog.reason"), required: true, multiline: true};
    const fields = {
        approve: [],
        reopen: [reasonField],
        cancel: [reasonField],
        hold: [reasonField],
        release_hold: [reasonField],
        adjust: [
            {name: "amount", label: t("money.settlements.dialog.amount"), required: true, inputMode: "decimal"},
            reasonField,
        ],
        send_to_bank: [
            {name: "reason", label: t("money.settlements.dialog.bankReference"), required: true},
        ],
        partial_paid: [
            {name: "amount", label: t("money.settlements.dialog.amount"), required: true, inputMode: "decimal"},
            {name: "reference", label: t("money.settlements.dialog.paymentReference"), required: true},
        ],
        mark_paid: [
            {name: "reference", label: t("money.settlements.dialog.paymentReference"), required: true},
        ],
    };
    return fields[action] || [];
};

const payloadFor = {
    approve: () => undefined,
    adjust: ({amount, reason}) => ({
        amount: normalizeMoney(amount, {allowNegative: true}),
        reason,
    }),
    partial_paid: ({amount, reference}) => ({
        amount: normalizeMoney(amount),
        payment_reference: reference,
    }),
    mark_paid: ({reference}) => ({payment_reference: reference}),
    send_to_bank: ({reason}) => ({reason}),
};

export const buildSettlementCommandPayload = (action, values) => {
    const builder = payloadFor[action];
    if (builder) return builder(values);
    if (["reopen", "cancel", "hold", "release_hold"].includes(action)) {
        return {reason: values.reason};
    }
    throw new Error(i18n.t("money.settlements.actions.unknownCommand"));
};

const payoutDetails = (action, statement, currency, t) => {
    if (!statement) return [{label: t("money.settlements.dialog.payable"), value: "—"}];
    if (!PAYMENT_ACTIONS.has(action)) {
        return [
            {
                label: t("money.settlements.dialog.payable"),
                value: formatExactMoney(statement.payable_amount, currency),
            },
        ];
    }
    const adjustedTotal = addMoney([
        statement.payable_amount,
        statement.adjustment_amount,
    ]);
    return [
        {
            label: t("money.settlements.dialog.baseAmount"),
            value: formatExactMoney(statement.payable_amount, currency),
        },
        {
            label: t("money.settlements.dialog.adjustment"),
            value: formatExactMoney(statement.adjustment_amount, currency),
        },
        {
            label: t("money.settlements.dialog.totalPayable"),
            value: formatExactMoney(adjustedTotal, currency),
        },
    ];
};

export const SettlementCommandDialog = ({
    action,
    statement,
    pending,
    error,
    fallbackFocusRef,
    onCancel,
    onConfirm,
}) => {
    const {t} = useTranslation();
    const presentation = getSettlementActionPresentation(action);
    const status = getSettlementStatusPresentation(statement?.status);
    const currency = statement?.snapshot?.currency?.code || "";
    const actionLabel = presentation
        ? t(`money.settlements.actions.${action}`, {defaultValue: presentation.label})
        : t("money.settlements.actions.fallback");
    const statusLabel = t(`money.settlements.statuses.${status.known ? statement?.status : "unknown"}`);
    return (
        <FinancialCommandDialog
            open={Boolean(action && statement && presentation)}
            dialogKey={`${statement?.id || ""}:${action || ""}`}
            title={actionLabel}
            description={t("money.settlements.dialog.description")}
            entity={t("money.settlements.dialog.entity", {id: statement?.id || ""})}
            details={[
                {label: t("money.settlements.dialog.company"), value: statement?.company_name || "—"},
                {label: t("money.settlements.dialog.currentStatus"), value: statusLabel},
                ...payoutDetails(action, statement, currency, t),
            ]}
            fields={commandFields(action, t)}
            pending={pending}
            error={error}
            fallbackFocusRef={fallbackFocusRef}
            confirmLabel={t("common.confirmation.confirm")}
            onCancel={onCancel}
            onConfirm={onConfirm}
        />
    );
};
